import axios from 'axios';
import NavigationBackButton from "@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue";
import { GET_QUESTIONS_IN_PROJECT,
        ADD_QUESTION_TO_ENQUIRY_FORM,
        GET_ENQUIRY_FORM_QUESTIONS ,
        REMOVE_QUESTION_TO_ENQUIRY_FORM,
        get_headers,
        get_project_id } from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import formatting_mixins from '@/utils/mixins/formatting_mixins';
import AICampusFooter from '@/components/probaworkflows_base_components/AICampusFooter/AiFooter.vue';

export default {
  name: 'ManageQuestions',
  mixins: [alert_mixin, formatting_mixins],
  components: {
    AICampusFooter,
    NavigationBackButton
  },
  data: () => ({
    project_id: get_project_id(),
    question_list: [],
    form_data: {},
    headers: get_headers(),
    submit_url: '',
    form_name: '',
    enquiry_form_id: '',
    starting_display_order: '',
    assigned_question_ids: [],
    table_headers: ['ID', 'Question', 'Type', 'Choices', 'Assigned'],
    is_loading: false
  }),
  async mounted() {
    await this.initialize_page();
  },
  methods: {
    async initialize_page() {
      this.is_loading = true;
      this.form_name = sessionStorage.getItem('form_name');
      this.enquiry_form_id = sessionStorage.getItem('enquiry_form_id') || '';
      
      if (!this.enquiry_form_id) {
        this.show_error("No enquiry form selected. Please select a form first.");
        this.is_loading = false;
        return;
      }

      // Reset state
      this.assigned_question_ids = [];
      this.question_list = [];
      
      // Get assigned questions first to ensure we have the correct state
      await this.get_assigned_questions();
      // Then get all questions and apply the assignment state
      await this.get_questions();
      
      this.is_loading = false;
    },

    create_questions() {
      this.$router.push('/manage_questions');
    },
    
    select_enquiry_form(id) {
      sessionStorage.setItem('enquiry_form_id', id);
    },

    async get_questions() {
      const payload = { project_id: this.project_id };
      const response = await axios.post(GET_QUESTIONS_IN_PROJECT, payload, {
        headers: this.headers
      });

      const response_data = typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;

      if(response_data.status === 'success'){
        // Create the question list with proper assignment state
        this.question_list = response_data.data.map(question => {
          const is_assigned = this.assigned_question_ids.includes(question.id);
          return {
            id: question.id,
            question_text: question.question_text,
            question_type: this.format_question_type(question.question_type),
            choices: question.multi_select_choices?.map(choice => choice.choice).join(', ') || '-',
            is_assigned: is_assigned,
            is_loading: false
          };
        });

      } else {
        this.show_error(`Error loading questions: ${response_data.message || 'Unknown error'}`);
      }
    },
    
    async toggle_assignment(question) {
      if (!this.enquiry_form_id) {
        this.show_error("Missing enquiry form ID — please select a form first.");
        return;
      }

      // Store the original state in case we need to revert
      const original_state = question.is_assigned;
      
      // Immediately update UI for better UX
      question.is_assigned = !original_state;
      question.is_loading = true;

      try {
        if (original_state) {
          // Was assigned, now unassigning
          await this.unassign_question(question.id);
        } else {
          // Was unassigned, now assigning
          await this.assign_question(question.id);
        }
        
        this.show_success(`Question successfully ${original_state ? 'unassigned from' : 'assigned to'} the form.`);
      } catch (error) {
        // Revert UI state on error
        question.is_assigned = original_state;
        this.show_error(`Error: ${error.response?.data?.message || error.message}`);
      } finally {
        question.is_loading = false;
      }
    },

    async assign_question(question_id) {
      const payload = {
        enquiry_form_id: this.enquiry_form_id,
        question_ids: [question_id],
        starting_display_order: 0,
      };

      const response = await axios.post(ADD_QUESTION_TO_ENQUIRY_FORM, payload, { 
        headers: this.headers 
      });

      // Update local state - ensure no duplicates
      if (!this.assigned_question_ids.includes(question_id)) {
        this.assigned_question_ids.push(question_id);
      }
      
      // Update the specific question in the list using Reflect
      const question_index = this.question_list.findIndex(q => q.id === question_id);
      if (question_index !== -1) {
        const question = Reflect.get(this.question_list, question_index);
        Reflect.set(question, 'is_assigned', true);
      }

      return response;
    },

    async unassign_question(question_id) {
      const payload = {
        enquiry_form_id: this.enquiry_form_id,
        question_ids: [question_id]
      };

      const config = {
        headers: this.headers
      };

      const response = await axios.post(REMOVE_QUESTION_TO_ENQUIRY_FORM, payload, config);
      
      // Update local state
      this.assigned_question_ids = this.assigned_question_ids.filter(id => id !== question_id);
      
      // Update the specific question in the list using Reflect
      const question_index = this.question_list.findIndex(q => q.id === question_id);
      if (question_index !== -1) {
        const question = Reflect.get(this.question_list, question_index);
        Reflect.set(question, 'is_assigned', false);
      }

      return response;
    },

    async get_assigned_questions() {
      if (!this.enquiry_form_id) {
        this.assigned_question_ids = [];
        return;
      }

      const response = await axios.get(GET_ENQUIRY_FORM_QUESTIONS, {
        params: { project_id: this.project_id },
        headers: this.headers
      });
      
      const response_data = response.data?.data || response.data || {};
      const questions = Array.isArray(response_data?.questions)
        ? response_data.questions
        : Array.isArray(response_data)
          ? response_data
          : [];

      const extracted_ids = questions
        .map(item => {
          // Try multiple possible paths to get the question ID
          return item?.question?.id || 
                 item?.id || 
                 item?.question_id ||
                 item?.question_details?.id;
        })
        .filter(id => id !== undefined && id !== null);

      this.assigned_question_ids = [...new Set(extracted_ids)];
    }
  },
 watch: {
    // Watch for enquiry form ID changes and reload data
    enquiry_form_id(newVal) { if (newVal) this.initialize_page(); }
  }
};